import { PatientLayout } from "@/components/layout/patient-layout";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, Trash2, Loader2, CheckCircle2, AlertTriangle, User, Shield, Clock, Ban, PauseCircle, Mail } from "lucide-react";
import { BrowserStorageCard } from "@/components/ui/browser-storage-card";

interface DataSummary {
  conversations: number;
  messages: number;
  aiAuditLogs: number;
  lastAiInteraction: string | null;
}

interface DeleteResult {
  success: boolean;
  deleted: { conversations: number; messages: number; aiAuditLogs: number };
  deletedAt: string;
  confirmationEmailSent?: boolean;
}

type RetentionDays = 30 | 90 | 180 | null;

const RETENTION_OPTIONS: { value: RetentionDays; label: string; description: string }[] = [
  { value: 30, label: "30 days", description: "Conversations older than 30 days are deleted automatically" },
  { value: 90, label: "90 days", description: "Conversations older than 90 days are deleted automatically (default)" },
  { value: 180, label: "180 days", description: "Conversations older than 180 days are deleted automatically" },
  { value: null, label: "Keep until I delete", description: "Your conversations are kept until you delete them manually" },
];

export default function PatientSettings() {
  const [summary, setSummary] = useState<DataSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  type DeleteStage = "idle" | "confirm" | "awaitingCode" | "done";
  const [deleteStage, setDeleteStage] = useState<DeleteStage>("idle");
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);
  const [requestingCode, setRequestingCode] = useState(false);
  const [codeRequestInfo, setCodeRequestInfo] = useState<{ maskedEmail: string; expiresAt: string } | null>(null);
  const [otpInput, setOtpInput] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);

  const [retentionDays, setRetentionDays] = useState<RetentionDays>(90);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionSaved, setRetentionSaved] = useState(false);
  const [retentionError, setRetentionError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/user/data/summary")
      .then((r) => r.json())
      .then((data) => { setSummary(data); setSummaryLoading(false); })
      .catch(() => { setSummaryError("Could not load your data summary."); setSummaryLoading(false); });

    fetch("/api/user/preferences")
      .then((r) => r.json())
      .then((data: { retentionDays: RetentionDays }) => {
        setRetentionDays(data.retentionDays);
        setRetentionLoading(false);
      })
      .catch(() => { setRetentionLoading(false); });
  }, []);

  const handleRetentionSave = async (value: RetentionDays) => {
    setRetentionSaving(true);
    setRetentionError(null);
    setRetentionSaved(false);
    try {
      const response = await fetch("/api/user/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ retentionDays: value }),
      });
      if (!response.ok) throw new Error("Failed to save preference");
      setRetentionDays(value);
      setRetentionSaved(true);
      setTimeout(() => setRetentionSaved(false), 3000);
    } catch {
      setRetentionError("Could not save your preference. Please try again.");
    } finally {
      setRetentionSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch("/api/user/data/export");
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rheumhub-data-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSummaryError("Could not export your data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleRequestDeletionCode = async () => {
    setRequestingCode(true);
    setDeleteError(null);
    try {
      const response = await fetch("/api/user/data/delete/request", { method: "POST" });
      const data = await response.json();
      if (!response.ok) {
        setDeleteError(data?.error ?? "Could not send confirmation email. Please try again.");
        return;
      }
      setCodeRequestInfo({ maskedEmail: data.maskedEmail, expiresAt: data.expiresAt });
      setOtpInput("");
      setDeleteStage("awaitingCode");
    } catch {
      setDeleteError("Could not send confirmation email. Please try again.");
    } finally {
      setRequestingCode(false);
    }
  };

  const handleConfirmDelete = async () => {
    const otp = otpInput.trim();
    if (!otp) {
      setDeleteError("Please enter the 6-digit confirmation code from your email.");
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      const response = await fetch("/api/user/data", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp }),
      });
      const data = await response.json();
      if (!response.ok) {
        setDeleteError(data?.error ?? "Could not delete your data. Please try again.");
        return;
      }
      setDeleteResult(data);
      setSummary({ conversations: 0, messages: 0, aiAuditLogs: 0, lastAiInteraction: null });
      setDeleteStage("done");
      setCodeRequestInfo(null);
      setOtpInput("");
    } catch {
      setDeleteError("Could not delete your data. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeletion = () => {
    setDeleteStage("idle");
    setOtpInput("");
    setDeleteError(null);
    setCodeRequestInfo(null);
  };

  return (
    <PatientLayout title="Your Data & Settings">
      <div className="max-w-2xl mx-auto space-y-8">

        <div className="flex items-start gap-4">
          <div className="bg-rose-50 p-3 rounded-2xl">
            <User className="h-7 w-7 text-rose-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Your Data</h2>
            <p className="text-slate-500 text-sm mt-0.5">You have the right to see, download, or delete your data at any time under UK GDPR.</p>
          </div>
        </div>

        <section className="bg-rose-50 rounded-3xl border border-rose-100 p-8 space-y-4">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-rose-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-slate-900">Your privacy</h3>
              <p className="text-sm text-slate-600 mt-1">
                Your chat conversations with the RheumHub AI guide are stored securely and automatically deleted after your chosen retention period. You can change this at any time below.
              </p>
              <p className="text-sm text-slate-600 mt-2">
                If you speak to a specialist nurse through RheumHub, nurses are required to keep professional records for longer as part of their NMC obligations — but your own patient chat history follows the window you set.
              </p>
              <p className="text-sm text-slate-600 mt-2">
                You can also delete all your data immediately at any time using the section below, or download a copy to keep.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 p-8 space-y-5">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-rose-400 shrink-0 mt-0.5" />
            <h3 className="font-bold text-slate-900">Chat history retention</h3>
          </div>
          <p className="text-sm text-slate-600">
            Choose how long your AI chat history is kept before it is automatically deleted. This gives you more control over your data.
          </p>

          {retentionLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading your preference…</div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {RETENTION_OPTIONS.map((option) => {
                const selected = retentionDays === option.value;
                return (
                  <button
                    key={String(option.value)}
                    onClick={() => handleRetentionSave(option.value)}
                    disabled={retentionSaving}
                    className={[
                      "w-full text-left rounded-2xl border px-5 py-4 transition-all",
                      selected
                        ? "border-rose-400 bg-rose-50 ring-1 ring-rose-400"
                        : "border-slate-200 bg-white hover:border-rose-200 hover:bg-rose-50/40",
                      retentionSaving ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className={["font-semibold text-sm", selected ? "text-rose-600" : "text-slate-800"].join(" ")}>
                          {option.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
                      </div>
                      <div className={[
                        "h-4 w-4 rounded-full border-2 shrink-0",
                        selected ? "border-rose-500 bg-rose-500" : "border-slate-300 bg-white",
                      ].join(" ")} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {retentionSaving && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Saving…
            </div>
          )}
          {retentionSaved && !retentionSaving && (
            <div className="flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" /> Preference saved.
            </div>
          )}
          {retentionError && (
            <p className="text-sm text-red-600">{retentionError}</p>
          )}
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900">Your data summary</h3>
          {summaryLoading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
          ) : summaryError ? (
            <p className="text-red-600 text-sm">{summaryError}</p>
          ) : summary ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-rose-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-slate-900">{summary.conversations}</p>
                <p className="text-sm text-slate-500">Conversations with the AI guide</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-slate-900">{summary.messages}</p>
                <p className="text-sm text-slate-500">Messages sent and received</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-4 col-span-2">
                <p className="text-sm font-semibold text-slate-900">
                  {summary.lastAiInteraction
                    ? new Date(summary.lastAiInteraction).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                    : "No conversations yet"}
                </p>
                <p className="text-sm text-slate-500">Last chat session</p>
              </div>
            </div>
          ) : null}
          <p className="text-xs text-slate-400">
            {retentionDays === null
              ? "Your chat data is kept until you delete it manually."
              : `Your chat data is stored for up to ${retentionDays} days, then automatically deleted.`}
          </p>
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900">Download your data</h3>
          <p className="text-sm text-slate-600">
            You can download a copy of all your conversations and messages. This is your right under UK GDPR (right to data portability). The file will be in JSON format.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-rose-500 hover:bg-rose-600 text-white rounded-full px-6"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Download my data
          </Button>
        </section>

        <section className="bg-white rounded-3xl border border-red-100 p-8 space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            Delete all my data
          </h3>
          <p className="text-sm text-slate-600">
            This will permanently delete all your conversations and messages from RheumHub. This cannot be undone. Your account will remain active.
          </p>

          {deleteStage === "done" && deleteResult ? (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold">All done — your data has been deleted.</p>
                <p>We removed {deleteResult.deleted.conversations} conversation{deleteResult.deleted.conversations !== 1 ? "s" : ""} and {deleteResult.deleted.messages} message{deleteResult.deleted.messages !== 1 ? "s" : ""}.</p>
                {deleteResult.confirmationEmailSent && (
                  <p className="text-green-700">A confirmation email has been sent to your registered address for your records.</p>
                )}
              </div>
            </div>
          ) : deleteStage === "awaitingCode" ? (
            <div className="space-y-3">
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800 space-y-2">
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-rose-500" />
                  <p>
                    We've sent a 6-digit confirmation code to <strong>{codeRequestInfo?.maskedEmail}</strong>. Enter it below to permanently delete your data. The code expires in about 15 minutes.
                  </p>
                </div>
              </div>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Confirmation code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="mt-1 block w-full max-w-[200px] rounded-xl border border-slate-300 px-4 py-3 text-lg tracking-[0.4em] font-semibold text-center focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400"
                />
              </label>
              {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleConfirmDelete}
                  disabled={deleting || otpInput.length < 4}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Confirm and delete everything
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRequestDeletionCode}
                  disabled={deleting || requestingCode}
                  className="rounded-full px-6"
                >
                  {requestingCode ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Resend code
                </Button>
                <Button variant="ghost" onClick={cancelDeletion} disabled={deleting} className="rounded-full px-6">
                  Cancel
                </Button>
              </div>
            </div>
          ) : deleteStage === "confirm" ? (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
                <p className="font-semibold">Are you sure you want to delete everything?</p>
                <p>For your safety we'll first email you a 6-digit confirmation code. You'll need to enter it before any data is actually deleted.</p>
              </div>
              {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
              <div className="flex gap-3">
                <Button
                  onClick={handleRequestDeletionCode}
                  disabled={requestingCode}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6"
                >
                  {requestingCode ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                  Email me a confirmation code
                </Button>
                <Button variant="outline" onClick={cancelDeletion} disabled={requestingCode} className="rounded-full px-6">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => { setDeleteError(null); setDeleteStage("confirm"); }}
              className="border-red-200 text-red-500 hover:bg-red-50 rounded-full px-6"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete all my data
            </Button>
          )}
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 p-8 space-y-4">
          <div className="flex items-start gap-3">
            <Ban className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h3 className="font-bold text-slate-900">Right to Object (Article 21)</h3>
              <p className="text-sm text-slate-600">
                Where we process your data based on our legitimate interests, you have the right to object at any time. We will stop processing your data unless we have compelling reasons to continue.
              </p>
              <a
                href="mailto:privacy@medantixhealth.co.uk?subject=Right%20to%20Object%20-%20Article%2021%20UK%20GDPR"
                className="inline-block text-sm font-medium text-rose-600 underline hover:text-rose-700"
              >
                Email privacy@medantixhealth.co.uk to object
              </a>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 p-8 space-y-4">
          <div className="flex items-start gap-3">
            <PauseCircle className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h3 className="font-bold text-slate-900">Right to Restrict Processing (Article 18)</h3>
              <p className="text-sm text-slate-600">
                You can ask us to pause how we use your data — for example, if you think it is inaccurate or you have objected to our use of it. While restricted, we will only store your data and not use it.
              </p>
              <a
                href="mailto:privacy@medantixhealth.co.uk?subject=Right%20to%20Restrict%20Processing%20-%20Article%2018%20UK%20GDPR"
                className="inline-block text-sm font-medium text-rose-600 underline hover:text-rose-700"
              >
                Email privacy@medantixhealth.co.uk to request a restriction
              </a>
            </div>
          </div>
        </section>

        <BrowserStorageCard cardClassName="bg-white rounded-3xl border border-slate-200 p-8 space-y-4" />

        <p className="text-center text-xs text-slate-400">
          Questions about your data? Contact <strong>privacy@medantixhealth.co.uk</strong>
          {" · "}
          <a href="/privacy" className="underline hover:text-slate-600">Privacy Policy</a>
        </p>

      </div>
    </PatientLayout>
  );
}
